import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminLayout from './layouts/AdminLayout';
import {
  Building,
  Mail,
  Phone,
  Clock,
  RefreshCw,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  FileText,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    'New': { color: 'yellow', icon: AlertTriangle },
    'In Progress': { color: 'blue', icon: Clock },
    'Contacted': { color: 'green', icon: CheckCircle },
    'Closed': { color: 'red', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig['New'];
  const Icon = config.icon;

  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    red: 'bg-red-100 text-red-800 border-red-200'
  };

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${colorClasses[config.color]}`}>
      <Icon className="w-4 h-4 mr-1.5" />
      {status}
    </span>
  );
};

const StatCard = ({ title, value, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    red: "bg-red-50 text-red-700 border-red-200"
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center">
        <Icon className="w-8 h-8 mr-3" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm font-medium">{title}</p>
        </div>
      </div>
    </div>
  );
};

const AdminEmployerContacts = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data, isLoading, error, refetch } = useQuery(
    ['employerContacts', currentPage, statusFilter, searchTerm],
    async () => {
      const response = await fetch(
        `${API_URL}/employer-contacts?page=${currentPage}&status=${statusFilter}&search=${searchTerm}`
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await response.json();
      return result;
    }
  );

  const { data: stats } = useQuery('employerContactStats', async () => {
    const response = await fetch(`${API_URL}/employer-contacts/stats`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const result = await response.json();
    
    if (!result.statusBreakdown) {
      result.statusBreakdown = { new: 0, inProgress: 0, contacted: 0, closed: 0 };
    }
    
    return result;
  });

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [note, setNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');

  const handleViewHistory = (contact) => {
    setSelectedContact(contact);
    setShowHistoryModal(true);
  };

  const updateContactStatus = async (id, newStatus, note = '', nextFollowUp = null) => {
    try {
      const payload = {
        status: newStatus,
        note,
        updatedBy: localStorage.getItem('userEmail')
      };
      if (nextFollowUp && !isNaN(new Date(nextFollowUp).getTime())) {
        payload.nextFollowUpDate = nextFollowUp;
      }
      const response = await fetch(`${API_URL}/employer-contacts/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      
      setShowNoteModal(false);
      setSelectedContact(null);
      setNote('');
      setFollowUpDate('');
      refetch();
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  const handleStatusChange = (contact, newStatus) => {
    setSelectedContact({
      ...contact,
      newStatus: newStatus
    });
    setShowNoteModal(true);
  };

  const handleNoteSubmit = () => {
    if (selectedContact) {
      updateContactStatus(
        selectedContact._id,
        selectedContact.newStatus,
        note,
        followUpDate
      );
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Employer Contacts
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {error.message}
            </p>
            <div className="mt-6">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | Employer Contacts</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin-dashboard"
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back to Dashboard
              </Link>
              <div className="h-6 border-l border-gray-300"></div>
              <h1 className="text-2xl font-bold text-gray-900">Employer Contacts</h1>
            </div>
            
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Contacts"
              value={stats.totalContacts || 0}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="New"
              value={stats.statusBreakdown?.new || 0}
              icon={AlertTriangle}
              color="yellow"
            />
            <StatCard
              title="In Progress"
              value={stats.statusBreakdown?.inProgress || 0}
              icon={Clock}
              color="blue"
            />
            <StatCard
              title="Contacted"
              value={stats.statusBreakdown?.contacted || 0}
              icon={CheckCircle}
              color="green"
            />
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary appearance-none"
              >
                <option value="">All Statuses</option>
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Help Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.contacts?.map((contact) => (
                  <tr key={contact._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Building className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="font-medium text-gray-900">{contact.companyName}</div>
                          <div className="text-gray-500">{contact.laborType}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {contact.firstName} {contact.lastName}
                        </div>
                        <div className="text-gray-500 flex items-center">
                          <Mail className="h-4 w-4 mr-1" />
                          {contact.companyEmail}
                        </div>
                        <div className="text-gray-500 flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {contact.companyPhone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900">{contact.helpType}</div>
                      <div className="text-gray-500">{contact.estimatedStaffCount} staff needed</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusChange(contact, e.target.value)}
                          className="border rounded p-1 w-full focus:ring-primary focus:border-primary"
                        >
                          <option value="New">New</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Closed">Closed</option>
                        </select>
                        {contact.lastContactedAt && (
                          <div className="text-xs text-gray-500 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            Last Contact: {new Date(contact.lastContactedAt).toLocaleDateString()}
                          </div>
                        )}
                        {contact.nextFollowUpDate && (
                          <div className="text-xs text-gray-500 flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            Follow-up: {new Date(contact.nextFollowUpDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm text-gray-500 flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            Submitted: {contact.submittedAt ? new Date(contact.submittedAt).toLocaleDateString() : 'N/A'}
                          </div>
                          <button
                            onClick={() => handleViewHistory(contact)}
                            className="text-primary hover:text-primary-dark text-sm flex items-center"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            History
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!data || currentPage >= (data.totalPages || 1)}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{data?.totalPages || 1}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!data || currentPage >= (data.totalPages || 1)}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* History Modal */}
        {showHistoryModal && selectedContact && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Contact History - {selectedContact?.companyName || 'Unknown Company'}
                  </h3>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    ×
                  </button>
                </div>
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {selectedContact.statusHistory?.map((history, index) => (
                    <div key={index} className="border-l-2 border-primary pl-4 pb-4">
                      <div className="text-sm font-medium text-gray-900">
                        Status: <StatusBadge status={history.status} />
                      </div>
                      <div className="text-xs text-gray-500">
                        {history.updatedAt ? new Date(history.updatedAt).toLocaleString() : 'N/A'}
                      </div>
                      {history.note && (
                        <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {history.note}
                        </div>
                      )}
                    </div>
                  ))}
                  {(!selectedContact.statusHistory || selectedContact.statusHistory.length === 0) && (
                    <p className="text-gray-500 text-center">No history available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Note Modal */}
        {showNoteModal && selectedContact && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Update Status
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status Note
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border border-gray-300 rounded-md"
                    placeholder="Add details about this status change..."
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Next Follow-up Date
                  </label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border border-gray-300 rounded-md"
                  />
                </div>
                <div className="mt-5 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowNoteModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNoteSubmit}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEmployerContacts;
